import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, Download, AlertTriangle, Ban, AlertCircle,
  CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Ruler, Info,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ---------------------------------------------------------------------------
// "En çok satan ürünlerin bedeni bitti mi?"
//
// Sol taraf GA4 ürün cirosu, sağ taraf hebiar feed'inin BUGÜNKÜ beden × stok
// durumu; ikisi `item_id = feed_id` ile birleşiyor (son 30 günde satan 488
// ürünün 488'i feed'de — ölçüldü 24 Ağu 2026).
//
// 🔴 Satış BEDEN kırılımında yok: GA4'ün `item_variant` alanı bedeni değil rengi
// taşıyor. "41 numara kaç sattı" hiçbir kaynakta olmadığı için sayfa şunu
// söylüyor: bu ürün para kazandırıyor, bugün kaç bedeni ayakta ve merdivenin
// ortası duruyor mu.
// ---------------------------------------------------------------------------

// Risk bandı ölçeği ORDINAL: tek hue (kırmızı), açıktan koyuya, "sorun yok" için
// nötr gri. dataviz `validate_palette.js --ordinal --surface #ffffff` ile
// doğrulandı (monoton L ✓, adım aralığı ✓, açık uç 2,05:1 ✓, hue yayılımı 5°).
// Sağlıklı bandı kırmızının en açığı DEĞİL nötr: "hiçbir şey olmamış" durumunun
// rengi yoktur.
const BAND_STYLE = {
  ok: { bg: '#e5e4df', ink: '#3f3f46', icon: CheckCircle2, label: 'Sağlıklı (≤%25)' },
  mid: { bg: '#e9a49b', ink: '#4a1513', icon: AlertCircle, label: '%25-50 kırık' },
  half: { bg: '#dd7565', ink: '#ffffff', icon: AlertCircle, label: '%50-75 kırık' },
  critical: { bg: '#d03b3b', ink: '#ffffff', icon: AlertTriangle, label: 'Kritik (%75+)' },
  dead: { bg: '#96201f', ink: '#ffffff', icon: Ban, label: 'Hiç bedeni yok' },
  nofeed: { bg: '#f4f4f5', ink: '#71717a', icon: AlertCircle, label: "Feed'de yok" },
};
// Şeritte ve tabloda AYNI sıra: iyiden kötüye değil, kötüden iyiye — okuma sırası
// "önce para nerede yanıyor" olsun diye.
const BAND_ORDER = ['dead', 'critical', 'half', 'mid', 'ok', 'nofeed'];

// Ürün satırının durum rozeti. `core` = merdivenin orta yarısı komple bitmiş:
// kırık yüzdesi orta seviyede olsa bile ürün fiilen satılamaz durumda.
const STATUS_STYLE = {
  dead: { ...BAND_STYLE.dead, label: 'Hiç beden yok' },
  core: { bg: '#d03b3b', ink: '#ffffff', icon: AlertTriangle, label: 'Orta beden bitti' },
  critical: { ...BAND_STYLE.critical, label: 'Kritik' },
  watch: { ...BAND_STYLE.half, label: 'Zayıflıyor' },
  ok: { ...BAND_STYLE.ok, label: 'Sağlıklı' },
  nofeed: { ...BAND_STYLE.nofeed, label: "Feed'de yok" },
};

const bandOf = (p) => {
  if (!p.inFeed) return 'nofeed';
  if (p.inStockSizes === 0) return 'dead';
  if (p.breakage > 75) return 'critical';
  if (p.breakage > 50) return 'half';
  if (p.breakage > 25) return 'mid';
  return 'ok';
};

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => `${Math.round(Number(n) || 0).toLocaleString('tr-TR')} ₺`;
const fmtMoneyShort = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v >= 1000000) return `${(v / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M ₺`;
  if (v >= 1000) return `${(v / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B ₺`;
  return `${v} ₺`;
};
const fmtPct = (v, d = 1) => (v == null ? '—' : `%${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: d })}`);
const fmtDay = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};

const RANGES = [
  { key: 7, label: 'Son 7 gün' },
  { key: 30, label: 'Son 30 gün' },
  { key: 60, label: 'Son 60 gün' },
  { key: 90, label: 'Son 90 gün' },
];
// Haftalık matriste hücre rengi. Bant eşikleriyle AYNI skala — iki sekme arasında
// gidip gelirken renk anlamı değişmesin.
function heatFor(b) {
  if (b == null) return null;
  if (b <= 25) return BAND_STYLE.ok;
  if (b <= 50) return BAND_STYLE.mid;
  if (b <= 75) return BAND_STYLE.half;
  if (b < 100) return BAND_STYLE.critical;
  return BAND_STYLE.dead;
}
const fmtWeek = (k) => {
  try {
    const a = new Date(`${k}T00:00:00`);
    const b = new Date(a); b.setDate(b.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return `${f(a)}–${f(b)}`;
  } catch { return k; }
};
const fmtDayShort = (k) => {
  try {
    return new Date(`${k}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  } catch { return k; }
};
// Günlük matriste hücre ~42px; "45B ₺" sığmıyor, para birimi düşürülüyor
// (kolon başlığı ve tooltip zaten ₺ diyor).
const fmtCellMoney = (v, dense) => (dense ? fmtMoneyShort(v).replace(' ₺', '') : fmtMoneyShort(v));

const GRAINS = [
  { key: 'week', label: 'Haftalık' },
  { key: 'day', label: 'Günlük' },
];
const DAY_RANGES = [14, 30, 60, 90];

const WINDOWS = [
  { key: 28, label: '4 haftalık pencere', hint: 'daha kararlı · ort. hata 0,8 beden' },
  { key: 14, label: '2 haftalık pencere', hint: 'daha çevik · ort. hata 1,05 beden' },
];

const SORTS = [
  { key: 'revenue', label: 'Ciro (çoktan aza)' },
  { key: 'risk', label: 'Riskli ciro (bitmiş × ciro)' },
  { key: 'breakage', label: 'Beden kırığı (çoktan aza)' },
  { key: 'qty', label: 'Satış adedi' },
  { key: 'drop', label: 'Ciro düşüşü (son 7 gün)' },
];

export default function High5TopSellerSizeStatus() {
  const { selectedBrand } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [bandFilter, setBandFilter] = useState('');   // şeride tıklayınca dolar
  const [coreOnly, setCoreOnly] = useState(false);
  const [sort, setSort] = useState('revenue');
  const [open, setOpen] = useState(() => new Set());

  const [tab, setTab] = useState('now');          // 'now' | 'weekly'
  const [grain, setGrain] = useState('week');    // 'week' | 'day'
  const [dayRange, setDayRange] = useState(30);
  const [windowDays, setWindowDays] = useState(28);
  const [weekly, setWeekly] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklyLimit, setWeeklyLimit] = useState(50);

  const fetchData = useCallback(async () => {
    if (!selectedBrand?.id) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/sku-feed/top-seller-size-status?days=${days}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message); setData(null);
    } finally { setLoading(false); }
  }, [selectedBrand?.id, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Haftalık veri ayrı uçtan ve SADECE sekme açılınca çekiliyor — ilk yüklemede
  // iki ağır sorgu birden çalışmasın.
  const fetchWeekly = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setWeeklyLoading(true);
    try {
      const qs = new URLSearchParams({ weeks: 'all', window: String(windowDays), grain });
      if (grain === 'day') qs.set('days', String(dayRange));
      const r = await fetch(
        `${API_BASE}/api/sku-feed/top-seller-size-status-weekly?${qs.toString()}`,
        { credentials: 'include', headers: { 'X-Brand-ID': selectedBrand.id } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setWeekly(await r.json());
    } catch (e) {
      setWeekly({ success: false, error: e.message });
    } finally { setWeeklyLoading(false); }
  }, [selectedBrand?.id, windowDays, grain, dayRange]);

  useEffect(() => { if (tab === 'weekly') fetchWeekly(); }, [tab, fetchWeekly]);

  // useMemo bağımlılığı olarak kullanıldığı için kimliği sabit kalmalı; düz
  // `data?.products || []` her render'da yeni dizi üretip pivotları boşuna
  // yeniden hesaplatıyordu.
  const products = useMemo(() => data?.products || [], [data]);
  const summary = data?.summary || null;

  const brands = useMemo(() => {
    const m = new Map();
    for (const p of products) {
      const b = p.brand || '(markasız)';
      m.set(b, (m.get(b) || 0) + p.revenue);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([b]) => b);
  }, [products]);

  const rows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr');
    let list = products.filter(p => {
      if (bandFilter && bandOf(p) !== bandFilter) return false;
      if (brandFilter && (p.brand || '(markasız)') !== brandFilter) return false;
      if (coreOnly && !(p.coreSizes > 0 && p.coreInStock === 0)) return false;
      if (needle) {
        const hay = `${p.name || ''} ${p.ga4Name || ''} ${p.brand || ''} ${p.itemId || ''}`.toLocaleLowerCase('tr');
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    const risk = (p) => p.revenue * ((p.breakage ?? 0) / 100);
    const cmp = {
      revenue: (a, b) => b.revenue - a.revenue,
      risk: (a, b) => risk(b) - risk(a),
      breakage: (a, b) => (b.breakage ?? -1) - (a.breakage ?? -1) || b.revenue - a.revenue,
      qty: (a, b) => b.quantity - a.quantity,
      // Düşüş: önceki hafta hiç satmayanı "düştü" saymamak için null'lar dibe.
      drop: (a, b) => (a.revDeltaPct ?? 1e9) - (b.revDeltaPct ?? 1e9),
    }[sort];
    return [...list].sort(cmp);
  }, [products, q, brandFilter, bandFilter, coreOnly, sort]);

  const filteredRevenue = rows.reduce((a, p) => a + p.revenue, 0);

  // Haftalık sekmede aynı arama/marka süzgeçleri geçerli; bant ve "orta beden"
  // süzgeçleri bugünkü duruma ait olduğu için burada UYGULANMIYOR (haftalık
  // hücrede o bilgi yok, uygulasaydım sessizce yanlış küme gösterirdi).
  const weeklyRows = useMemo(() => {
    const list = weekly?.products || [];
    const needle = q.trim().toLocaleLowerCase('tr');
    return list.filter(p => {
      if (brandFilter && (p.brand || '(markasız)') !== brandFilter) return false;
      if (!needle) return true;
      return `${p.name || ''} ${p.brand || ''} ${p.itemId || ''}`.toLocaleLowerCase('tr').includes(needle);
    });
  }, [weekly, q, brandFilter]);

  const downloadCsv = () => {
    const head = ['Ürün', 'Marka', 'item_id', `Ciro (${days}g)`, 'Adet', 'Son 7g ciro', 'Önceki 7g ciro',
      'Toplam beden', 'Stokta beden', 'Kırık %', 'Orta beden', 'Orta bedende stokta', 'Durum',
      'Stokta olan bedenler', 'Biten bedenler'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [head.map(esc).join(',')];
    for (const p of rows) {
      lines.push([
        p.name, p.brand, p.itemId, Math.round(p.revenue), p.quantity,
        Math.round(p.revLast7 || 0), Math.round(p.revPrev7 || 0),
        p.totalSizes, p.inStockSizes, p.breakage ?? '', p.coreSizes, p.coreInStock,
        STATUS_STYLE[p.status]?.label || p.status,
        p.sizes.filter(s => s.inStock).map(s => s.size).join(' '),
        p.sizes.filter(s => !s.inStock).map(s => s.size).join(' '),
      ].map(esc).join(','));
    }
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `high5-satan-urun-beden-durumu-${data?.asOf || 'bugun'}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const noData = !loading && data && data.hasData === false;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* ------------------------------------------------------------------ */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-rose-50/70 to-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl border border-rose-200 bg-rose-50 flex items-center justify-center">
                <Ruler className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">En Çok Satan Ürünler — Beden Durumu</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Satış GA4'ten, beden × stok {data?.asOf ? <strong>{fmtDay(data.asOf)}</strong> : 'bugünkü'} feed
                  anlık görüntüsünden · Soru: para kazandıran üründe kaç beden ayakta kaldı
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl">
                {[{ k: 'now', l: 'Bugünkü durum' }, { k: 'weekly', l: 'Haftalık seyir' }].map(o => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setTab(o.k)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      tab === o.k ? 'bg-rose-600 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <div className={`inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl ${tab === 'weekly' ? 'hidden' : ''}`}>
                {RANGES.map(o => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setDays(o.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      days === o.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <button
                type="button" onClick={fetchData} disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
              <button
                type="button" onClick={downloadCsv} disabled={!rows.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5" />
                CSV
              </button>
            </div>
          </div>
        </div>

        {/* KPI şeridi: soru "kaç ürün" değil "ne kadar para" — her kutuda ciro var. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
          <Tile
            label={`Son ${days} günde satan`}
            value={fmtInt(summary?.products)}
            unit="ürün"
            sub={summary ? `${fmtMoney(summary.revenue)} ciro · ${fmtInt(summary.quantity)} adet` : '—'}
          />
          <Tile
            label="Bedeni tamamen bitmiş"
            value={fmtInt(summary?.bands?.find(b => b.key === 'dead')?.products)}
            unit="ürün"
            tone="dead"
            sub={summary ? `${fmtMoney(summary.bands.find(b => b.key === 'dead')?.revenue)} · ciro payı ${fmtPct(summary.bands.find(b => b.key === 'dead')?.share)}` : '—'}
          />
          <Tile
            label="Orta bedeni bitmiş"
            value={fmtInt(summary?.coreGone?.products)}
            unit="ürün"
            tone="critical"
            sub={summary ? `${fmtMoney(summary.coreGone.revenue)} · merdivenin ortası komple yok` : '—'}
          />
          <Tile
            label="Beden verisi"
            value={data?.asOf ? fmtDay(data.asOf).replace(/ \d{4}$/, '') : '—'}
            sub={data?.history
              ? `${data.history.dates.length} günlük tarihçe${data.history.gapDays ? ` · ${data.history.gapDays} gün eksik` : ''}`
              : '—'}
            muted={!!data?.history?.gapDays}
          />
        </div>

        {/* Tarihçedeki boşluk, "stok değişmedi" diye okunmasın. */}
        {data?.history?.gapDays > 0 && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Beden feed'inin tarihçesinde <strong>{data.history.gapDays} gün eksik</strong> (besleme
              o günlerde kaynaktan 403 aldı). Sayfa <strong>bugünün durumunu</strong> gösteriyor;
              "beden ne zaman bitti" sorusu bu boşluk yüzünden cevaplanamıyor.
            </p>
          </div>
        )}

        {error && <div className="px-6 py-4 text-sm text-red-600">Veri alınamadı: {error}</div>}
        {noData && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            {data?.note || 'Bu markada beden feed\'i ya da ürün satış verisi yok.'}
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Risk şeridi: cironun tek satırlık parça-bütün dağılımı. Tek ölçü
          (ciro), tek eksen, sıralı tek hue — bant seçilince tablo süzülür. */}
      {tab === 'now' && summary && !noData && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Ciro nerede duruyor?</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Şeridin tamamı son {days} günün cirosu · Genişlik = o bandın ciro payı ·
              Bir banda tıkla, tablo o ürünlere insin
            </p>
          </div>
          <div className="px-6 py-5">
            <RiskBar
              bands={summary.bands}
              total={summary.revenue}
              active={bandFilter}
              onPick={(k) => setBandFilter(prev => (prev === k ? '' : k))}
            />
          </div>
        </Card>
      )}

      {/* ------------------------------------------------------------------ */}
      {tab === 'now' && !noData && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-semibold text-gray-800">Ürün ürün beden durumu</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Satıra tıkla, bedenler açılsın · <span className="text-gray-700 font-medium">Köşeli</span> yazılan
                bedenler merdivenin ortası · Üstü çizili = bugün stokta yok
              </p>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Ürün ara"
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-xl w-44 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                />
              </div>
              <select
                value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
                className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-xl bg-white max-w-[10rem]"
              >
                <option value="">Tüm markalar</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select
                value={sort} onChange={(e) => setSort(e.target.value)}
                className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-xl bg-white"
              >
                {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button
                type="button" onClick={() => setCoreOnly(v => !v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-xl border transition-colors ${
                  coreOnly ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Sadece orta bedeni bitmiş
              </button>
              {(bandFilter || brandFilter || coreOnly || q) && (
                <button
                  type="button"
                  onClick={() => { setBandFilter(''); setBrandFilter(''); setCoreOnly(false); setQ(''); }}
                  className="px-3 py-1.5 text-xs text-gray-500 underline underline-offset-2 hover:text-gray-800"
                >
                  filtreleri temizle
                </button>
              )}
            </div>
          </div>

          <div className="px-6 py-2 bg-gray-50/70 border-b border-gray-100 text-[11px] text-gray-500">
            <strong className="text-gray-700">{fmtInt(rows.length)}</strong> ürün ·{' '}
            <strong className="text-gray-700">{fmtMoney(filteredRevenue)}</strong> ciro
            {summary?.revenue > 0 && <> · toplam ciroda payı {fmtPct((filteredRevenue / summary.revenue) * 100)}</>}
            {bandFilter && <> · <span className="font-medium text-gray-700">{BAND_STYLE[bandFilter]?.label}</span> bandı</>}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50" lang="tr">
                <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2.5 text-left">Ürün</th>
                  <th className="px-3 py-2.5 text-right">Ciro ({days}g)</th>
                  <th className="px-3 py-2.5 text-right">Adet</th>
                  <th className="px-3 py-2.5 text-right">Son 7g</th>
                  <th className="px-3 py-2.5 text-right">Beden</th>
                  <th className="px-3 py-2.5 text-left w-40">Beden kırığı</th>
                  <th className="px-3 py-2.5 text-right">Orta beden</th>
                  <th className="px-4 py-2.5 text-left">Durum</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">Yükleniyor…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">Bu filtrede ürün yok.</td></tr>
                )}
                {!loading && rows.map(p => {
                  const isOpen = open.has(p.itemId);
                  return (
                    <React.Fragment key={p.itemId}>
                      <tr
                        className="border-t border-gray-100 hover:bg-rose-50/30 cursor-pointer align-middle"
                        onClick={() => setOpen(prev => {
                          const n = new Set(prev);
                          if (n.has(p.itemId)) n.delete(p.itemId); else n.add(p.itemId);
                          return n;
                        })}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            {isOpen
                              ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              : <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                            {p.image
                              ? <img src={p.image} alt="" loading="lazy" className="w-9 h-9 rounded-lg object-cover bg-gray-100 shrink-0" />
                              : <div className="w-9 h-9 rounded-lg bg-gray-100 shrink-0" />}
                            <div className="min-w-0">
                              <div className="text-[13px] font-medium text-gray-800 truncate max-w-[24rem]" title={p.name}>
                                {p.name}
                              </div>
                              <div className="text-[11px] text-gray-400 truncate">
                                {p.brand || '—'}{p.color ? ` · ${p.color}` : ''} · {p.itemId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtMoneyShort(p.revenue)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtInt(p.quantity)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          <div className="text-gray-700">{p.revLast7 ? fmtMoneyShort(p.revLast7) : <span className="text-gray-300">—</span>}</div>
                          {p.revDeltaPct != null && Math.abs(p.revDeltaPct) >= 1 && (
                            <div className={`text-[10px] font-medium ${p.revDeltaPct < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                              {p.revDeltaPct > 0 ? '+' : '−'}{fmtPct(Math.abs(p.revDeltaPct), 0)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          <span className={p.inStockSizes === 0 ? 'text-red-600 font-semibold' : 'text-gray-800 font-medium'}>
                            {p.inStockSizes}
                          </span>
                          <span className="text-gray-400"> / {p.totalSizes}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <BreakageMeter value={p.breakage} band={bandOf(p)} />
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {p.coreSizes === 0
                            ? <span className="text-gray-300" title="Tek bedenli ürün — merdiven yok">tek beden</span>
                            : (
                              <span className={p.coreInStock === 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                                {p.coreInStock}<span className="text-gray-400"> / {p.coreSizes}</span>
                              </span>
                            )}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusChip status={p.status} />
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50/70 border-t border-gray-100">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="flex items-start gap-3 flex-wrap">
                              <div className="flex flex-wrap gap-1.5 max-w-[68%]">
                                {p.sizes.map((s, i) => (
                                  <span
                                    key={`${s.size}-${i}`}
                                    title={`${s.size} · ${s.inStock ? 'stokta' : 'stok dışı'}${s.core ? ' · merdivenin ortası' : ''}`}
                                    className={`px-2 py-1 rounded-lg text-[11px] tabular-nums border ${
                                      s.inStock
                                        ? 'bg-white text-gray-800 border-gray-200'
                                        : 'bg-rose-50 text-rose-700 border-rose-200 line-through'
                                    } ${s.core ? 'ring-1 ring-gray-900/25 font-semibold' : ''}`}
                                  >
                                    {s.core ? `[${s.size}]` : s.size}
                                  </span>
                                ))}
                                {p.sizes.length === 0 && (
                                  <span className="text-xs text-gray-400">Bu ürün feed'de beden taşımıyor.</span>
                                )}
                                {p.totalSizes > 0 && (
                                  <span className="w-full text-[11px] text-gray-500 mt-1">
                                    Biten beden:{' '}
                                    <strong className="text-rose-700">
                                      {p.sizes.filter(s => !s.inStock).map(s => s.size).join(', ') || 'yok'}
                                    </strong>
                                  </span>
                                )}
                              </div>
                              <div className="ml-auto text-[11px] text-gray-500 space-y-1 text-right">
                                <div>
                                  Son satış: <strong className="text-gray-700">{fmtDay(p.lastSaleDate)}</strong>
                                </div>
                                <div>
                                  Fiyat: <strong className="text-gray-700">
                                    {p.salePrice != null && p.price != null && p.salePrice < p.price
                                      ? `${fmtMoney(p.salePrice)}`
                                      : fmtMoney(p.price)}
                                  </strong>
                                  {p.salePrice != null && p.price != null && p.salePrice < p.price && (
                                    <span className="ml-1 line-through text-gray-400">{fmtMoney(p.price)}</span>
                                  )}
                                </div>
                                {p.link && (
                                  <a
                                    href={p.link} target="_blank" rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                                  >
                                    Ürün sayfası <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-3 border-t border-gray-100 text-[11px] text-gray-400 space-y-1">
            <p>
              <strong className="text-gray-500">Beden kırığı</strong> = stok dışı beden / toplam beden.
              <strong className="text-gray-500"> Orta beden</strong> = ürünün kendi beden merdiveninin orta yarısı;
              36'sının olmaması ürünü öldürmez, 42-44'ünün olmaması öldürür.
            </p>
            <p>
              🔴 Satış verisi <strong className="text-gray-500">beden kırılımında yok</strong> — GA4 renk seviyesinde
              ölçüyor. "Hangi beden kaç sattı" sorusu bu veriyle cevaplanamaz; sayfa "ne kadar para kazandıran üründe
              kaç beden ayakta" sorusunu cevaplar.
            </p>
          </div>
        </Card>
      )}

      {/* ------------------------------------------------------------------ */}
      {tab === 'weekly' && (
        <WeeklyPanel
          data={weekly}
          rows={weeklyRows}
          loading={weeklyLoading}
          windowDays={windowDays}
          setWindowDays={setWindowDays}
          grain={grain} setGrain={setGrain}
          dayRange={dayRange} setDayRange={setDayRange}
          limit={weeklyLimit}
          setLimit={setWeeklyLimit}
          q={q} setQ={setQ}
          brandFilter={brandFilter} setBrandFilter={setBrandFilter}
          brands={brands}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Haftalık seyir: satır ürün, sütun hafta, hücrede ÜSTTE o haftanın cirosu,
// ALTTA beden kırığı. İki sayı da her zaman yazılı; renk sadece ikincil kodlama
// (kardeş sayfalarda oturmuş kural).
function WeeklyPanel({ data, rows, loading, windowDays, setWindowDays, grain, setGrain, dayRange, setDayRange, limit, setLimit, q, setQ, brandFilter, setBrandFilter, brands }) {
  const isDay = grain === 'day';
  const bucketLabel = isDay ? fmtDayShort : fmtWeek;
  const [sortKey, setSortKey] = useState('revenue');
  const [breakdown, setBreakdown] = useState('product');   // product | brand | brandCat
  const [strictCohort, setStrictCohort] = useState(false);

  const weeks = useMemo(() => data?.weeks || [], [data]);

  // 🔴 SABİT KOHORT seçeneği: haftadan haftaya ölçülebilen ürün kümesi değişince
  // marka satırı sahte sıçrıyor (kardeş sayfada %24,3 → %30,2 böyle çıkmıştı).
  // Açıkken sadece TÜM haftalarda ölçülmüş ürünler sayılır.
  const base = useMemo(() => {
    if (!strictCohort) return rows;
    return rows.filter(p => weeks.every(w => p.cells?.[w.key]?.b != null));
  }, [rows, weeks, strictCohort]);

  // Marka / marka × kategori satırları. Birleştirme EVREN AĞIRLIKLI: kırık
  // yüzdelerinin düz ortalaması 1 bedenli aksesuarı 17 bedenli koşu ayakkabısıyla
  // eşit sayardı. Ciro ve adet düz toplanır.
  const grouped = useMemo(() => {
    if (breakdown === 'product') return base;
    const m = new Map();
    for (const p of base) {
      const label = breakdown === 'brand'
        ? (p.brand || '(markasız)')
        : `${p.brand || '(markasız)'} · ${p.category || 'Diğer'}`;
      const key = breakdown === 'brand'
        ? (p.brandKey || 'yok')
        : `${p.brandKey || 'yok'}|${p.category || 'Diğer'}`;
      if (!m.has(key)) {
        m.set(key, {
          itemId: key, name: label, brand: p.brand, category: breakdown === 'brandCat' ? p.category : null,
          image: null, isGroup: true, products: 0, revenue: 0, quantity: 0, cells: {},
        });
      }
      const g = m.get(key);
      g.products++;
      g.revenue += p.revenue;
      g.quantity += p.quantity;
      for (const w of weeks) {
        const c = p.cells?.[w.key];
        if (!c) continue;
        const gc = g.cells[w.key] || { rev: 0, qty: 0, a: 0, u: 0, n: 0, e: false };
        gc.rev += c.rev || 0;
        gc.qty += c.qty || 0;
        if (c.b != null) { gc.a += c.a; gc.u += c.u; gc.n++; gc.e = gc.e || !!c.e; }
        g.cells[w.key] = gc;
      }
    }
    // Kırık yüzdesi ve Δ, birleştirme bittikten SONRA hesaplanır.
    return [...m.values()].map(g => {
      const measured = [];
      for (const w of weeks) {
        const c = g.cells[w.key];
        if (!c) continue;
        c.b = c.u > 0 ? Math.round((1 - c.a / c.u) * 1000) / 10 : null;
        if (c.b != null) measured.push(c);
      }
      const first = measured[0], last = measured[measured.length - 1];
      return {
        ...g,
        firstBreakage: first?.b ?? null,
        lastBreakage: last?.b ?? null,
        breakageDelta: first && last ? Math.round((last.b - first.b) * 10) / 10 : null,
      };
    });
  }, [base, breakdown, weeks]);

  const sorted = useMemo(() => {
    const cmp = {
      revenue: (a, b) => b.revenue - a.revenue,
      // 🔑 "Kötüleşen" CİRO AĞIRLIKLI: ham puan farkına göre sıralayınca liste
      // 48 ₺'lik şapkalarla doluyor (hepsi tek bedenli, 0 ↔ 100 arasında zıplıyor).
      // Sıralanan şey "kaç puan kötüleşti" değil "kötüleşme kaç liralık ciroyu vuruyor".
      worsening: (a, b) =>
        ((b.breakageDelta ?? 0) * b.revenue) - ((a.breakageDelta ?? 0) * a.revenue),
      last: (a, b) => (b.lastBreakage ?? -1) - (a.lastBreakage ?? -1) || b.revenue - a.revenue,
    }[sortKey];
    return [...grouped].sort(cmp);
  }, [grouped, sortKey]);

  const shown = breakdown === 'product' ? sorted.slice(0, limit) : sorted;
  const acc = data?.method?.accuracy;

  if (loading) {
    return (
      <Card className="rounded-2xl">
        <div className="px-6 py-16 text-center text-sm text-gray-400">Haftalık seyir hesaplanıyor…</div>
      </Card>
    );
  }
  if (data && data.success === false) {
    return (
      <Card className="rounded-2xl">
        <div className="px-6 py-10 text-center text-sm text-red-600">Haftalık veri alınamadı: {data.error}</div>
      </Card>
    );
  }
  if (!data || !weeks.length) {
    return (
      <Card className="rounded-2xl">
        <div className="px-6 py-10 text-center text-sm text-gray-500">Haftalık ölçüm için yeterli feed tarihçesi yok.</div>
      </Card>
    );
  }

  // Günlükte 30-90 sütun oluyor; hücre 2,6rem'e iniyor ve kart yatayda kayıyor.
  const cellW = isDay ? 2.6 : 4.5;
  const grid = { gridTemplateColumns: `18rem repeat(${weeks.length}, minmax(${cellW}rem, 1fr)) 5rem` };
  const minW = `${18 + weeks.length * cellW + 5}rem`;

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-800">{isDay ? 'Günlük' : 'Haftalık'} seyir — ciro ve beden kırığı</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Her hücrede <strong className="text-gray-700">üstte o {isDay ? 'günün' : 'haftanın'} cirosu</strong>,
            <strong className="text-gray-700"> altta beden kırığı</strong>
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {/* Tanecik → (günlükse) kaç gün → kırılım. Üçü aynı satırda çünkü
                birlikte "neye bakıyorum" sorusunun cevabını kuruyorlar. */}
            <div className="inline-flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl" lang="tr">
              {GRAINS.map(o => (
                <button
                  key={o.key} type="button" onClick={() => setGrain(o.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    grain === o.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {isDay && (
              <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                {DAY_RANGES.map(d => (
                  <button
                    key={d} type="button" onClick={() => setDayRange(d)}
                    className={`px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-colors ${
                      dayRange === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {d} gün
                  </button>
                ))}
              </div>
            )}
            <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl" lang="tr">
              {[
                { k: 'product', l: 'Ürün ürün' },
                { k: 'brand', l: 'Marka' },
                { k: 'brandCat', l: 'Marka × Ayakkabı/Diğer' },
              ].map(o => (
                <button
                  key={o.k} type="button" onClick={() => setBreakdown(o.k)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    breakdown === o.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün ara"
              className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-xl w-40 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
          </div>
          <select
            value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
            className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-xl bg-white max-w-[9rem]"
          >
            <option value="">Tüm markalar</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            value={sortKey} onChange={(e) => setSortKey(e.target.value)}
            className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-xl bg-white"
          >
            <option value="revenue">Ciro (çoktan aza)</option>
            <option value="worsening">En çok kötüleşen (ciro ağırlıklı)</option>
            <option value="last">Son hafta kırığı</option>
          </select>
          <button
            type="button" onClick={() => setStrictCohort(v => !v)}
            title="Açıkken sadece HER hafta ölçülebilmiş ürünler sayılır; haftadan haftaya ürün kümesi değişmez"
            className={`px-3 py-1.5 text-[11px] font-medium rounded-xl border transition-colors ${
              strictCohort ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Sabit kohort
          </button>
          <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
            {WINDOWS.map(o => (
              <button
                key={o.key} type="button" onClick={() => setWindowDays(o.key)}
                title={o.hint}
                className={`px-2.5 py-1.5 text-[11px] font-medium rounded-lg transition-colors ${
                  windowDays === o.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Yöntem uyarısı: hücre haftanın kendisi değil, geriye bakan pencere. */}
      <div className="px-6 py-2.5 bg-blue-50/70 border-b border-blue-100 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-900">
          Beden kırığı hücresi <strong>o {isDay ? 'gün' : 'hafta'} biterken geriye bakan {windowDays} günlük
          pencerede</strong> ölçülüyor, {isDay ? 'o günün kendisinde' : 'haftanın kendisinde'} değil. Sebebi: beden
          feed'i günde ürün başına tek beden gösteriyor, tek {isDay ? 'günlük' : 'haftalık'} örneklem 5 bedenli ürünle
          17 bedenliyi ayırt edemiyor.
          {acc && <> Kesin sayıma karşı ölçülen hata <strong>{String(acc.mae).replace('.', ',')} beden</strong>,
            korelasyon <strong>{String(acc.corr).replace('.', ',')}</strong>, ürünlerin
            %{String(acc.within1).replace('.', ',')}'i ±1 beden içinde.</>}
        </p>
      </div>

      {/* İki işaret, iki farklı bilgi kaynağı: ★ hebiar'ın kesin sayımı, ◆ ise
          ürünün o gün MC feed'inde hiç olmaması. MC yalnızca stokta olanı
          taşıdığı için ◆ tahmin değil, "bedeni kalmadı"nın doğrudan kanıtı. */}
      <div className="px-6 py-2.5 bg-white border-b border-gray-100 flex items-start gap-4 flex-wrap text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-[3px] ring-2 ring-emerald-500/60 inline-block" />
          <strong className="text-emerald-700">★ kesin sayım</strong> — beden feed'i o gün çalışmış
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-[3px] inline-block" style={{ background: BAND_STYLE.dead.bg }} />
          <strong className="text-rose-800">◆ reklam feed'inde yok</strong> — MC sadece stokta olanı taşır, ürün
          düşmüşse bedeni kalmamıştır (ölçüm: bu durumdakilerin gerçek kırığı ort. %93)
        </span>
        <span className="text-gray-400">gerisi kayan pencere tahmini</span>
      </div>

      <div className="px-6 py-4 overflow-x-auto">
        <div style={{ minWidth: minW }}>
          {/* Başlık */}
          <div className="grid gap-[3px] items-end" style={grid}>
            <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide pb-1" lang="tr">Ürün</div>
            {weeks.map(w => (
              <div key={w.key} className="text-center pb-1">
                <div className={`${isDay ? 'text-[9px]' : 'text-[10px]'} leading-tight ${w.exact ? 'text-emerald-700 font-semibold' : 'text-gray-500'}`}>
                  {w.exact && '★'}{bucketLabel(w.key)}
                </div>
                {w.partial && (
                  <div className="text-[9px] text-amber-600 font-medium">
                    {isDay ? 'bugün' : `kısmi (${w.days}/7 gün)`}
                  </div>
                )}
              </div>
            ))}
            <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide pb-1 text-right" lang="tr">Δ kırık</div>
          </div>

          {/* Toplam satırı EN ÜSTTE: portföyün geneli önce okunsun. */}
          <div className="grid gap-[3px] mt-1 pb-[6px] mb-[6px] border-b-2 border-gray-300" style={grid}>
            {/* Bu satır kasıtlı olarak SÜZGEÇTEN BAĞIMSIZ: marka satırını neye göre
                okuyacağını gösteren sabit ölçüt. Aksi hâlde "Hoka %50" iyi mi kötü
                mü sorusunun cevabı ekranda kalmıyor. */}
            <div className="text-xs font-bold text-gray-900 flex flex-col justify-center" lang="tr"
                 title="Portföyün tamamı — arama/marka/kohort süzgeçlerinden etkilenmez, kıyas ölçütü olarak sabit durur">
              TÜM ÜRÜNLER
              <span className="text-[9px] font-normal text-gray-400 normal-case">süzgeçten bağımsız kıyas</span>
            </div>
            {weeks.map(w => {
              const t = (data.totals || []).find(x => x.week === w.key);
              const step = heatFor(t?.breakage);
              return (
                <div
                  key={w.key}
                  title={t
                    ? `Tüm ürünler · ${bucketLabel(w.key)}\nCiro: ${fmtMoney(t.revenue)}${w.partial ? (isDay ? ' (bugün henüz bitmedi)' : ` (kısmi hafta, ${w.days}/7 gün)`) : ''}\nBeden kırığı: ${fmtPct(t.breakage)}\n${t.available}/${t.universe} beden · ${t.products} ürün\n${t.exact ? 'Kesin sayım' : 'Tahmin'}`
                    : 'Veri yok'}
                  className={`h-[42px] rounded-[3px] flex flex-col items-center justify-center gap-[2px] tabular-nums ring-1 ring-gray-900/10 ${
                    w.exact ? 'ring-2 ring-emerald-500/60' : ''
                  }`}
                  style={{ background: step ? step.bg : '#f4f4f5', color: step ? step.ink : '#a1a1aa' }}
                >
                  {t && (
                    <>
                      <span className={`${isDay ? 'text-[9px]' : 'text-[10px]'} font-medium ${w.partial ? 'italic opacity-60' : 'opacity-80'}`}>
                        {fmtCellMoney(t.revenue, isDay)}
                      </span>
                      <span className="text-[11px] font-bold">{fmtPct(t.breakage, 0)}</span>
                    </>
                  )}
                </div>
              );
            })}
            <div className="text-right text-xs font-bold text-gray-900 flex items-center justify-end">—</div>
          </div>

          {/* Ürün satırları */}
          <div className="space-y-[3px]">
            {shown.map(p => (
              <div key={p.itemId} className="grid gap-[3px]" style={grid}>
                <div className="flex items-center gap-2 pr-2 min-w-0">
                  {p.isGroup ? (
                    <div className="w-7 h-7 rounded-md bg-gray-900/5 border border-gray-200 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-gray-500 tabular-nums">{p.products}</span>
                    </div>
                  ) : p.image
                    ? <img src={p.image} alt="" loading="lazy" className="w-7 h-7 rounded-md object-cover bg-gray-100 shrink-0" />
                    : <div className="w-7 h-7 rounded-md bg-gray-100 shrink-0" />}
                  <div className="min-w-0">
                    <div className={`truncate ${p.isGroup ? 'text-[12px] font-semibold text-gray-800' : 'text-[11px] font-medium text-gray-700'}`} title={p.name}>
                      {p.name}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">
                      {p.isGroup ? `${p.products} ürün · ` : `${p.brand || '—'} · `}{fmtMoneyShort(p.revenue)}
                    </div>
                  </div>
                </div>
                {weeks.map(w => {
                  const c = p.cells?.[w.key];
                  const step = heatFor(c?.b);
                  const hasAny = c && (c.b != null || c.rev);
                  return (
                    <div
                      key={w.key}
                      title={hasAny
                        ? `${p.name}\n${bucketLabel(w.key)}${w.partial ? (isDay ? ' · bugün henüz bitmedi' : ` · kısmi hafta (${w.days}/7 gün)`) : ''}\n` +
                          `Ciro: ${fmtMoney(c.rev || 0)}${c.qty ? ` · ${c.qty} adet` : ''}\n` +
                          (c.b == null
                            ? 'Beden ölçümü yok (feed\'de yeterli gün yok)'
                            : `Beden kırığı: ${fmtPct(c.b)} · ${c.a}/${c.u} beden ayakta\n` +
                              (p.isGroup ? `${c.n} ürün ölçüldü\n` : '') +
                              (c.e ? 'KESİN sayım (beden feed\'i)'
                                   : c.g ? 'Ürün o gün reklam feed\'inde YOK — MC yalnızca stokta olanı taşır'
                                         : `Tahmin (${windowDays} günlük pencere)`))
                        : `${p.name}\n${bucketLabel(w.key)}\nVeri yok`}
                      className={`h-[42px] rounded-[3px] flex flex-col items-center justify-center gap-[2px] tabular-nums ${
                        c?.e ? 'ring-2 ring-emerald-500/60' : ''
                      }`}
                      style={{
                        background: step ? step.bg : (hasAny ? '#f4f4f5' : 'repeating-linear-gradient(45deg,#fafafa,#fafafa 3px,#f2f2f2 3px,#f2f2f2 6px)'),
                        color: step ? step.ink : '#a1a1aa',
                      }}
                    >
                      {hasAny && (
                        <>
                          <span className={`${isDay ? 'text-[9px]' : 'text-[10px]'} ${w.partial ? 'italic opacity-60' : 'opacity-75'}`}>
                            {c.rev ? fmtCellMoney(c.rev, isDay) : '·'}
                          </span>
                          <span className={`${isDay ? 'text-[10px]' : 'text-[11px]'} font-bold`}>
                            {c.b == null ? '—' : (c.g ? '◆' : '') + fmtPct(c.b, 0)}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
                <div className="text-right text-[11px] font-semibold tabular-nums flex items-center justify-end pl-1">
                  {p.breakageDelta == null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span className={p.breakageDelta > 5 ? 'text-red-600' : p.breakageDelta < -5 ? 'text-emerald-600' : 'text-gray-400'}>
                      {p.breakageDelta > 0 ? '+' : ''}{String(Math.round(p.breakageDelta)).replace('.', ',')} p
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {breakdown === 'product' && sorted.length > shown.length && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setLimit(l => l + 50)}
                className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                {fmtInt(sorted.length - shown.length)} ürün daha göster
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-gray-100 text-[11px] text-gray-400 space-y-1">
        <p>
          Taralı hücre = beden ölçümü yok · <strong className="text-gray-500">·</strong> = o {isDay ? 'gün' : 'hafta'} satış yok ·
          <strong className="text-rose-700"> ◆</strong> = ürün o gün reklam feed'inde yok ·
          <strong className="text-gray-500"> Δ kırık</strong> = ilk ölçülen haftadan son ölçülen haftaya puan farkı.
        </p>
        <p>
          Marka satırlarında kırık <strong className="text-gray-500">evren ağırlıklı</strong> birleştirilir (beden
          yüzdelerinin düz ortalaması değil) · <strong className="text-gray-500">Sabit kohort</strong> açıkken sadece her
          hafta ölçülebilmiş ürünler sayılır — haftadan haftaya ürün kümesi değiştiği için oluşan sahte sıçramayı keser.
        </p>
        <p>
          🔴 Ciro GA4'ten geliyor ve <strong className="text-gray-500">tamdır</strong>; beden kırığı ★ olmayan haftalarda
          tahmindir. Kısmi haftanın cirosu tam haftalarla yan yana okunamaz (italik yazılı).
        </p>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function Tile({ label, value, unit, sub, tone, muted }) {
  const t = tone ? BAND_STYLE[tone] : null;
  return (
    <div className="px-6 py-4">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5" lang="tr">
        {t && <span className="w-2 h-2 rounded-[2px]" style={{ background: t.bg }} />}
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <div className={`text-2xl font-semibold ${muted ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
        {unit && <span className="text-xs text-gray-400">{unit}</span>}
      </div>
      <div className="mt-0.5 text-[11px] text-gray-400">{sub}</div>
    </div>
  );
}

// Parça-bütün şerit. Segmentler arasında 2px yüzey boşluğu; sayı segmentin
// içinde değil altındaki efsanede — dar segmentte rakam kaybolmasın diye.
function RiskBar({ bands, total, active, onPick }) {
  const map = new Map(bands.map(b => [b.key, b]));
  const list = BAND_ORDER.map(k => map.get(k)).filter(b => b && b.revenue > 0);
  return (
    <>
      <div className="flex w-full h-9 gap-[2px]">
        {list.map(b => {
          const st = BAND_STYLE[b.key];
          const w = total > 0 ? (b.revenue / total) * 100 : 0;
          const dim = active && active !== b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => onPick(b.key)}
              title={`${st.label}\n${b.products} ürün · ${fmtMoney(b.revenue)} · ciro payı ${fmtPct(b.share)}`}
              className={`h-full rounded-[3px] transition-opacity ${dim ? 'opacity-30' : 'opacity-100'} ${
                active === b.key ? 'ring-2 ring-gray-900/50' : ''
              }`}
              style={{ width: `${w}%`, background: st.bg, minWidth: 6 }}
            >
              {w >= 10 && (
                <span className="text-[11px] font-semibold tabular-nums" style={{ color: st.ink }}>
                  {fmtPct(b.share, 1)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Efsane: renk tek başına anlam taşımıyor — ikon + etiket + sayı. */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2">
        {list.map(b => {
          const st = BAND_STYLE[b.key];
          const Icon = st.icon;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => onPick(b.key)}
              className={`text-left rounded-xl px-2.5 py-2 border transition-colors ${
                active === b.key ? 'border-gray-900 bg-gray-50' : 'border-transparent hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600" lang="tr">
                <span className="w-2.5 h-2.5 rounded-[2px] shrink-0" style={{ background: st.bg }} />
                <Icon className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="truncate">{st.label}</span>
              </div>
              <div className="mt-0.5 text-sm font-semibold text-gray-900 tabular-nums">{fmtMoneyShort(b.revenue)}</div>
              <div className="text-[10px] text-gray-400 tabular-nums">{fmtInt(b.products)} ürün · {fmtPct(b.share)}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// Satır içi ölçer: aynı ordinal skala, aynı renk. Rakam her zaman yazılı,
// okuma renge bırakılmıyor.
function BreakageMeter({ value, band }) {
  if (value == null) return <span className="text-gray-300 text-xs">—</span>;
  const st = BAND_STYLE[band] || BAND_STYLE.ok;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden min-w-[3.5rem]">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(2, Math.min(100, value))}%`, background: st.bg }}
        />
      </div>
      <span className="text-[11px] tabular-nums text-gray-600 w-10 text-right">{fmtPct(value, 0)}</span>
    </div>
  );
}

function StatusChip({ status }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.ok;
  const Icon = st.icon;
  const light = status === 'ok' || status === 'nofeed' || status === 'mid';
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap"
      style={{ background: st.bg, color: light ? st.ink : '#ffffff' }}
      lang="tr"
    >
      <Icon className="w-3 h-3" />
      {st.label}
    </span>
  );
}
